import { useTranslation } from "react-i18next";
import { Autocomplete, IconButton, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import { GomakeTextInput } from "@/components";
import { PrimaryButton } from "@/components/button/primary-button";
import { IOutputTranslation } from "../types";
import { useOutputsAdminModalStyle } from "./use-outputs-admin-modal-style";

export interface IOutputTranslationRow {
  id: string;
  lang: string;
  name: string;
}

const createTranslationRow = (
  lang = "",
  name = ""
): IOutputTranslationRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  lang,
  name,
});

const DEFAULT_LANG_KEYS = [
  "en",
  "he",
  "ar",
  "de",
  "js",
  "ru",
  "tr",
  "pt",
  "es",
  "pl",
  "sq",
];

const getDefaultTranslationRows = (): IOutputTranslationRow[] =>
  DEFAULT_LANG_KEYS.map((lang) => createTranslationRow(lang, ""));

const DEFAULT_TRANSLATION_ROWS: IOutputTranslationRow[] =
  getDefaultTranslationRows();

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "he", label: "עברית" },
  { code: "ar", label: "العربية" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" },
  { code: "ja", label: "日本語" },
  { code: "pl", label: "Polski" },
  { code: "sq", label: "Shqip" },
  { code: "ru", label: "Русский" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "zh", label: "中文" },
];

interface OutputTranslationsFieldsProps {
  rows: IOutputTranslationRow[];
  onChange: (rows: IOutputTranslationRow[]) => void;
  layout?: "list" | "picker";
}

const OutputTranslationsFields = ({
  rows,
  onChange,
  layout = "list",
}: OutputTranslationsFieldsProps) => {
  const { t } = useTranslation();
  const { classes } = useOutputsAdminModalStyle();

  const updateRow = (
    id: string,
    field: keyof Pick<IOutputTranslationRow, "lang" | "name">,
    value: string
  ) => {
    onChange(
      rows.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: field === "lang" ? value.toLowerCase().trim() : value,
            }
          : row
      )
    );
  };

  const addRow = () => {
    onChange([...rows, createTranslationRow()]);
  };

  const removeRow = (id: string) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((row) => row.id !== id));
  };

  if (layout === "picker") {
    const visible = rows.length ? rows : [];
    return (
      <Stack
        gap={1.5}
        sx={{
          border: "1px solid #E6E8F0",
          borderRadius: "12px",
          background: "#F7F8FF",
          p: 2,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <LanguageIcon sx={{ color: "#4C45D4" }} fontSize="small" />
            <Typography sx={{ fontWeight: 700, color: "#1F2268" }}>
              {t("products.addProduct.admin.translations", { defaultValue: "Translations" })}
            </Typography>
          </Stack>
          <PrimaryButton variant="text" startIcon={<AddIcon />} onClick={addRow} style={{ ...classes.addTranslationBtn, color: "#4C45D4" }}>
            {t("outputsAdmin.addTranslation")}
          </PrimaryButton>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" sx={{ px: 0.5 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
            <Typography variant="caption">{t("products.addProduct.admin.language", { defaultValue: "Language" })}</Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: "#5C6BC0" }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: 12, color: "#5C6BC0" }}>
                {t("products.addProduct.admin.freeListValueHint", { defaultValue: "Not listed? Type to add." })}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" sx={{ flex: 1 }}>
            {t("products.addProduct.admin.translationName", { defaultValue: "Translation name" })}
          </Typography>
          <span style={{ width: 40 }} />
        </Stack>
        {visible.map((row) => (
          <Stack key={row.id} direction="row" gap={1} alignItems="center">
            <Autocomplete
              freeSolo
              size="small"
              options={LANGUAGES}
              sx={{ flex: 1, background: "#fff" }}
              value={LANGUAGES.find((language) => language.code === row.lang) || row.lang || null}
              getOptionLabel={(option) => typeof option === "string" ? option : `${option.label} (${option.code})`}
              filterOptions={(options, state) => {
                const languages = options.filter((option): option is { code: string; label: string } => typeof option !== "string");
                const query = state.inputValue.trim().toLowerCase();
                const filtered = query
                  ? languages.filter((option) => option.code.toLowerCase().includes(query) || option.label.toLowerCase().includes(query))
                  : languages;
                const typed = state.inputValue.trim();
                const known = languages.some((option) => option.code.toLowerCase() === typed.toLowerCase() || option.label.toLowerCase() === typed.toLowerCase());
                if (typed && !known) return [...filtered, typed];
                return filtered;
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  {typeof option === "string"
                    ? t("products.addProduct.admin.addTypedValue", { value: option, defaultValue: `Add "${option}"` })
                    : `${option.label} (${option.code})`}
                </li>
              )}
              onChange={(_event, value) => {
                updateRow(row.id, "lang", !value ? "" : typeof value === "string" ? value.trim() : value.code);
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === "input") updateRow(row.id, "lang", value);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder={t("products.addProduct.admin.selectLanguage", { defaultValue: "Select language" })} />
              )}
            />
            <TextField
              size="small"
              sx={{ flex: 1, background: "#fff" }}
              placeholder={t("products.addProduct.admin.enterTranslationName", { defaultValue: "Enter translation name" })}
              value={row.name}
              onChange={(e) => updateRow(row.id, "name", e.target.value)}
            />
            <IconButton
              size="small"
              aria-label={t("outputsAdmin.removeTranslation")}
              onClick={() => onChange(rows.filter((item) => item.id !== row.id))}
              sx={{ color: "#D32F2F" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <div style={classes.translationList} className="custom-scrollbar">
        {rows.map((row) => (
          <div key={row.id} style={classes.translationRow}>
            <GomakeTextInput
              style={classes.textInput}
              placeholder={t("outputsAdmin.langKey")}
              value={row.lang}
              onChange={(e) => updateRow(row.id, "lang", e.target.value)}
            />
            <GomakeTextInput
              style={classes.textInput}
              placeholder={t("outputsAdmin.translationName")}
              value={row.name}
              onChange={(e) => updateRow(row.id, "name", e.target.value)}
            />
            <IconButton
              size="small"
              onClick={() => removeRow(row.id)}
              disabled={rows.length <= 1}
              aria-label={t("outputsAdmin.removeTranslation")}
              sx={{ color: "#D32F2F" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>

      <PrimaryButton
        variant="text"
        startIcon={<AddIcon />}
        onClick={addRow}
        style={classes.addTranslationBtn}
      >
        {t("outputsAdmin.addTranslation")}
      </PrimaryButton>
    </Stack>
  );
};

const buildTranslationsPayload = (
  rows: IOutputTranslationRow[]
): IOutputTranslation[] =>
  rows
    .map((row) => ({
      lang: row.lang.trim().toLowerCase(),
      name: row.name.trim(),
    }))
    .filter((item) => item.lang.length > 0 && item.name.length > 0);

const getPrimaryOutputName = (rows: IOutputTranslationRow[]): string => {
  const payload = buildTranslationsPayload(rows);
  const en = payload.find((item) => item.lang === "en");
  if (en) return en.name;
  return payload[0]?.name ?? "";
};

const hasValidTranslations = (rows: IOutputTranslationRow[]): boolean =>
  buildTranslationsPayload(rows).length > 0;

const rowsFromTranslations = (
  translations: IOutputTranslation[] = []
): IOutputTranslationRow[] => {
  const byLang = new Map(
    translations.map((item) => [item.lang.toLowerCase(), item.name])
  );
  const defaults = getDefaultTranslationRows().map((row) => ({
    ...row,
    name: byLang.get(row.lang) ?? "",
  }));
  const extras = translations
    .filter((item) => !DEFAULT_LANG_KEYS.includes(item.lang.toLowerCase()))
    .map((item) => createTranslationRow(item.lang, item.name));
  return [...defaults, ...extras];
};

export {
  OutputTranslationsFields,
  buildTranslationsPayload,
  getPrimaryOutputName,
  hasValidTranslations,
  DEFAULT_TRANSLATION_ROWS,
  createTranslationRow,
  getDefaultTranslationRows,
  rowsFromTranslations,
};
